import React, {useState, useEffect, useMemo} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Space } from 'antd';
import SummaryCard from '../../summaryCard';
import { CalculusUtils } from "../../../utils/calculus";
import { getComparisonData } from '../../../stateManagement/actions/report';
import { useLocation } from "react-router-dom";

// Initial data state with a function to reset
const getInitialData = () => ({
    revenue: 0,
    impressions: 0,
    viewability: 0,
    measurableViewability: 0,
    measurableCTR: 0
});

// Initial compare state with a function to reset
const getInitialCompare = () => ({
    revenue: 0,
    impressions: 0,
    viewability: 0,
})

const MainCardSection = ({fullData, trigger, currentState, limited = false, dashboardFilters, partition, cardMetrics, reportScope, detailQuery}) => {
    
    const dispatch = useDispatch();
    const location = useLocation();
    
    const isDealsPage = location.pathname === '/thd/deals';
    
    const [data, setData] = useState(getInitialData());
    const [compareData, setCompareData] = useState(getInitialCompare());
    const [comparisonData, setComparisonData] = useState([]);

    // Extrating report-related state
    const reportReducer = useSelector((state) => state.reportReducer);
    const {comparison, period, filterBy: legacyFilterBy, publisherView} = reportReducer;
    const filterBy = legacyFilterBy || '';

    // Use the same applied filters for both periods and discard stale responses.
    useEffect(() => {
        let active = true;
        setComparisonData([]);
        if (comparison && publisherView) {
            dispatch(getComparisonData(
                {period, filterBy, publisherView, isDealsPage, dashboardFilters, partition, reportScope, ...detailQuery, totalsOnly: true},
                result => {if (active) setComparisonData(result);}
            ));
        }
        return () => {active = false;};
    }, [dispatch, period, filterBy, comparison, publisherView, isDealsPage, dashboardFilters, partition, reportScope, detailQuery]);

    // Effect to calculate metrics when data changes
    useEffect(() => {
        if (cardMetrics) { setData(cardMetrics); return; }
        // Early return if no data
        if (!fullData.length) {
            setData(getInitialData());
            return;
        }

        // Calculate key metrics
        const calculateMetrics = () => {
            const revenue = CalculusUtils.sumObjectProps(fullData, "revenue");
            const impressions = CalculusUtils.sumObjectProps(fullData, "impressions");
            const viewableImpressions = CalculusUtils.sumObjectProps(fullData, "viewableImpressions");
            const measurableImpressions = CalculusUtils.sumObjectPropsByNotZero(fullData, "impressions", "viewableImpressions");
            const measurableCTRImps = CalculusUtils.sumObjectPropsByThreshold(fullData, "impressions", "clicks", 10000);
            
            return {
                revenue,
                impressions,
                // Prevent division by zero
                viewability: measurableImpressions > 0 ? (viewableImpressions / measurableImpressions) * 100 : 0,
                measurableViewability: measurableImpressions > 0 ? (measurableImpressions / impressions) * 100 : 0,
                measurableCTR: measurableCTRImps > 0 ? (measurableCTRImps / impressions) * 100 : 0,
            };
        };
    
        setData(calculateMetrics());
    }, [fullData, cardMetrics]);

    //Effect to calculate set comparison data
    useEffect(() => {
        // Early return if no data
        if (!comparisonData.length) {
            setCompareData(getInitialCompare());
            return;
        }
        const refData = comparisonData[0];
        const {revenue, impressions, viewability} = refData;
        // Calculate key metrics
        const calculateMetrics = () => {
            return {
                revenue: revenue[1],
                impressions: impressions[1],
                viewability: viewability[1],
            };
        };
    
        setCompareData(calculateMetrics());
    }, [comparisonData]);
    
    // Memoize the rendering of cards to prevent unnecessary re-renders
    const renderCards = useMemo(() => (
        <>
            <SummaryCard 
                title="Revenue (€)" 
                content={data.revenue} 
                action={() => trigger("revenue")} 
                selected={currentState === "revenue"} 
                tooltipText="100% coverage"
                comparisonState={comparison}
                comparisonValue={compareData.revenue}
            />
            <SummaryCard 
                title="Impressions" 
                content={data.impressions} 
                action={() => trigger("impressions")} 
                selected={currentState === "impressions"} 
                tooltipText="100% coverage"
                comparisonState={comparison}
                comparisonValue={compareData.impressions}
            />
            <SummaryCard 
                title="Viewability (%)" 
                content={!limited ? data.viewability : "No data"} 
                selectable={false} 
                tooltipText={!limited ? `${(data.measurableViewability).toFixed(0)}% coverage` : null}
                comparisonState={!limited ? comparison : null}
                comparisonValue={!limited ? compareData.viewability : null}
                disabled={limited}
            />
        </>
    ), [data, currentState, limited, trigger, compareData, comparison]);

    return (
        <Space className="main-cards-wrapper" style={{width: "12.5rem"}}>
            {renderCards}
        </Space>
    );
}

export default MainCardSection;